import { useEffect, useMemo, useState } from "react";
import {
  Clock,
  Plus,
  Pencil,
  Trash2,
  User,
  GitCompare,
  Tag,
  Rocket,
  CheckCircle2,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  getPppcActivity,
  PppcActivityAction,
  PppcActivityEntry,
} from "@/services/pppcActivityLog";
import { PppcCompareDialog } from "./PppcCompareDialog";
import { useSavePppcPolicy } from "@/hooks/usePppcPolicy";
import { toast } from "sonner";
import {
  formatInTimezone,
  formatRelativeTime,
} from "@/lib/timezonePreference";
import { useTimezonePreference } from "@/hooks/useTimezonePreference";
import { TimezoneSelect } from "@/components/system/TimezoneSelect";

interface PppcActivityLogProps {
  policyId: string;
}

const ACTION_META: Record<
  PppcActivityAction,
  { label: string; icon: typeof Plus; tone: string }
> = {
  created: { label: "Created", icon: Plus, tone: "bg-success-muted text-success" },
  updated: { label: "Updated", icon: Pencil, tone: "bg-primary/10 text-primary" },
  deleted: { label: "Deleted", icon: Trash2, tone: "bg-destructive/10 text-destructive" },
  tagged: { label: "Tags changed", icon: Tag, tone: "bg-accent text-accent-foreground" },
  deployed: { label: "Deployed", icon: Rocket, tone: "bg-primary/10 text-primary" },
  installed: {
    label: "Installed on device",
    icon: CheckCircle2,
    tone: "bg-success-muted text-success",
  },
};

function formatTimestamp(
  iso: string,
  pref: ReturnType<typeof useTimezonePreference>,
): { absolute: string; relative: string } {
  return {
    absolute: formatInTimezone(iso, pref),
    relative: formatRelativeTime(iso),
  };
}

export function PppcActivityLog({ policyId }: PppcActivityLogProps) {
  const tz = useTimezonePreference();
  const [entries, setEntries] = useState<PppcActivityEntry[]>(() =>
    getPppcActivity(policyId),
  );

  useEffect(() => {
    setEntries(getPppcActivity(policyId));
    const refresh = (e: Event) => {
      const detail = (e as CustomEvent<{ policyId: string }>).detail;
      if (!detail || detail.policyId === policyId) {
        setEntries(getPppcActivity(policyId));
      }
    };
    const onStorage = (e: StorageEvent) => {
      if (e.key === "pppc-activity-log:v1") setEntries(getPppcActivity(policyId));
    };
    window.addEventListener("pppc-activity-updated", refresh);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("pppc-activity-updated", refresh);
      window.removeEventListener("storage", onStorage);
    };
  }, [policyId]);

  const snapshotEntries = useMemo(
    () => entries.filter((e) => !!e.snapshot && e.action !== "deleted"),
    [entries],
  );
  const canCompare = snapshotEntries.length >= 2;
  const [compareOpen, setCompareOpen] = useState(false);

  const saveMutation = useSavePppcPolicy();

  const handleRevert = async (previous: PppcActivityEntry) => {
    if (!previous.snapshot) return;
    const snap = previous.snapshot;
    try {
      await saveMutation.mutateAsync({
        existingPolicyId: policyId,
        appName: snap.appName,
        bundleId: snap.identifier,
        identifierType: snap.identifierType,
        codeRequirement: snap.codeRequirement,
        choices: snap.choices,
      });
      toast.success("Reverted to previous snapshot");
    } catch {
      // useSavePppcPolicy already shows an error toast.
    }
  };

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-2 space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Clock className="h-4 w-4 text-muted-foreground" />
          Activity Log
        </CardTitle>
        <div className="flex items-center gap-2">
          <TimezoneSelect className="h-9 w-[200px]" />
          <Button
            variant="outline"
            size="sm"
            disabled={!canCompare}
            onClick={() => setCompareOpen(true)}
            title={
              canCompare
                ? "Compare the latest two snapshots"
                : "Need at least two updates with snapshots to compare"
            }
          >
            <GitCompare className="h-4 w-4 mr-2" />
            Compare with previous
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        {entries.length === 0 ? (
          <div className="text-sm text-muted-foreground py-8 text-center">
            No activity recorded yet. Save a change to start tracking history.
          </div>
        ) : (
          <ol className="relative border-l border-border ml-3 space-y-6">
            {entries.map((e) => {
              const meta = ACTION_META[e.action];
              const Icon = meta.icon;
              const ts = formatTimestamp(e.at, tz);
              return (
                <li key={e.id} className="ml-6">
                  <span
                    className={`absolute -left-3 flex h-6 w-6 items-center justify-center rounded-full ${meta.tone} ring-4 ring-background`}
                  >
                    <Icon className="h-3 w-3" />
                  </span>
                  <div className="flex flex-wrap items-center gap-2 mb-1">
                    <Badge variant="outline" className="text-xs">
                      {meta.label}
                    </Badge>
                    <span
                      className="text-xs text-muted-foreground"
                      title={ts.absolute}
                    >
                      {ts.relative} · {ts.absolute}
                    </span>
                  </div>
                  {e.summary && (
                    <p className="text-sm text-foreground">{e.summary}</p>
                  )}
                  <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1">
                    <User className="h-3 w-3" />
                    {e.actor ?? "Unknown user"}
                  </p>
                </li>
              );
            })}
          </ol>
        )}
      </CardContent>
      <PppcCompareDialog
        open={compareOpen}
        onOpenChange={setCompareOpen}
        current={snapshotEntries[0] ?? null}
        previous={snapshotEntries[1] ?? null}
        onRevert={handleRevert}
        isReverting={saveMutation.isPending}
      />
    </Card>
  );
}
